import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { Event, Router, NavigationStart } from "@angular/router";
import { TokenCookieService } from "./core/service/token-storage-cookies.service";
import {
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
  MatSnackBar,
} from "@angular/material/snack-bar";
import { Subscription, interval, switchMap, EMPTY } from "rxjs";
import { AuthService } from "./core/service/auth.service";
import { IdleTimer } from "./core/service/idletime.service";
import { NotificationService } from "./shared/services/notification.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [IdleTimer],
})
export class AppComponent implements OnInit, OnDestroy {
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
    }
  }

  currentUrl: string;
  horizontalPosition: MatSnackBarHorizontalPosition = "end";
  verticalPosition: MatSnackBarVerticalPosition = "top";
  private routerEventsSubscription: Subscription;
  private tokenRefreshSubscription: Subscription;
  private userSubscription: Subscription;
  private currentUser: any;
  private isUserIdle = false;

  constructor(
    private tokenCookieService: TokenCookieService,
    private snackBar: MatSnackBar,
    private idleTimer: IdleTimer,
    private router: Router,
    private authService: AuthService,
    private notificationAPI: NotificationService
  ) {
    this.snackBar.dismiss();
    console.log('[AppComponent] Constructor initialized.');

    // Subscribe to user changes
    this.userSubscription = this.tokenCookieService.user$.subscribe(user => {
      console.log('[AppComponent] User updated:', user);
      this.currentUser = user;
      if (user) {
        console.log('[AppComponent] User detected, starting idle timer and token refresh.');
        this.setupIdleTimer();
        this.startTokenRefresh();
      } else {
        console.log('[AppComponent] No user, stopping idle timer.');
        this.idleTimer.stop();
        if (this.tokenRefreshSubscription) {
          this.tokenRefreshSubscription.unsubscribe();
          console.log('[AppComponent] Token refresh unsubscribed due to no user.');
        }
      }
    });

    this.routerEventsSubscription = this.router.events.subscribe(
      (routerEvent: Event) => {
        if (routerEvent instanceof NavigationStart) {
          this.currentUrl = routerEvent.url.substring(
            routerEvent.url.lastIndexOf("/") + 1
          );
          console.log('[AppComponent] Navigation to:', this.currentUrl);
        }
      }
    );
  }

  ngOnInit(): void {
    console.log('[AppComponent] ngOnInit called.');
  }

  private setupIdleTimer(): void {
    console.log('[AppComponent] Setting up idle timer with 20-minute timeout.');
    this.idleTimer.start({
      timeout: 60 * 20, 
      onTimeout: () => {
        console.log('[AppComponent] Idle timeout triggered.');
        this.isUserIdle = true;
        this.logout();
      },
    });
  }

  private startTokenRefresh(): void {
    const REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes
    console.log('[AppComponent] Starting token refresh interval.');

    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
      console.log('[AppComponent] Previous token refresh unsubscribed.');
    }

    this.tokenRefreshSubscription = interval(REFRESH_INTERVAL)
      .pipe(
        switchMap(() => {
          if (this.currentUser && this.currentUser.refreshToken && !this.isUserIdle) {
            console.log('[AppComponent] Refreshing token:', this.currentUser.refreshToken);
            return this.authService.refreshAccessToken(this.currentUser.refreshToken);
          } else {
            console.warn('[AppComponent] Skipping token refresh: No user/refreshToken or idle.');
            return EMPTY;
          }
        })
      )
      .subscribe(
        (response) => {
          if (response) {
            console.log('[AppComponent] Token refresh successful:', response);
            this.tokenCookieService.saveUser(response);
          }
        },
        (error) => {
          console.error('[AppComponent] Token refresh failed:', error);
          this.router.navigate(["/signin"]);
        }
      );
  }

  private logout(): void {
    console.log('[AppComponent] Initiating logout.');
    const refreshToken = this.currentUser?.refreshToken;
    if (!refreshToken) {
      console.warn('[AppComponent] No refresh token, redirecting to signin.');
      this.router.navigate(["/signin"]);
      return;
    }

    console.log('[AppComponent] Calling signOut with refreshToken:', refreshToken);
    this.tokenCookieService.signOut(refreshToken).subscribe(
      (res) => {
        console.log('[AppComponent] Logout API response:', res);
        this.router.navigate(["/signin"]);
        if (res.statusCode === 200) {
          this.notificationAPI.alertSuccess(res.message);
        } else {
          this.notificationAPI.alertWarning(res.message);
        }
      },
      (err) => {
        console.error('[AppComponent] Logout API error:', err);
        this.router.navigate(["/signin"]);
        this.notificationAPI.alertWarning(err.message || 'Logout failed.');
      }
    );
  }

  ngOnDestroy(): void {
    console.log('[AppComponent] Destroying component.');
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
      console.log('[AppComponent] Token refresh unsubscribed.');
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
      console.log('[AppComponent] Router events unsubscribed.');
    }
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
      console.log('[AppComponent] User subscription unsubscribed.');
    }
    if (this.idleTimer?.stop) {
      this.idleTimer.stop();
      console.log('[AppComponent] Idle timer stopped.');
    }
  }
}